import React from "react";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Target, Eye, Heart } from "lucide-react";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Banner */}
      <section className="bg-primary text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/29039798/pexels-photo-29039798.jpeg?auto=compress&cs=tinysrgb&w=2000"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-widest text-accent font-semibold">About Us</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-4" style={{ fontFamily: "Outfit" }}>
            Who We Are
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto">
            Indapur Farmers Producer Company is a farmer-led organization working for the prosperity of the farming
            community.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1605000797499-95a51c5269ae?auto=format&fit=crop&w=1000&q=80"
              alt="Indapur FPC"
              className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
            />
          </div>
          <div>
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Story</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-5" style={{ fontFamily: "Outfit" }}>
              A farmer-led organization rooted in Indapur
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Indapur Farmers Producer Company (FPC) was formed by progressive farmers of the Indapur taluka to
              collectively improve their productivity, reduce costs and unlock better market opportunities.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              We believe in the strength of collective enterprise. By bringing together farmers, providing them with
              quality inputs, modern training and direct market access, we aim to build a prosperous and sustainable
              farming community in Maharashtra.
            </p>
            <ul className="space-y-3">
              {[
                "Farmer empowerment through collective strength",
                "Quality products and services at fair prices",
                "Adoption of modern agricultural technologies",
                "Sustainable and profitable farming",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" /> <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Mission Vision Values */}
      <section className="py-16 md:py-20 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: "Our Mission", text: "Empower farmers with quality inputs, knowledge and market access for sustainable prosperity." },
            { icon: Eye, title: "Our Vision", text: "To be a leading farmer producer company in Maharashtra, recognized for transforming rural livelihoods." },
            { icon: Heart, title: "Our Values", text: "Integrity, transparency, collective strength and unwavering commitment to the farming community." },
          ].map(({ icon: Icon, title, text }) => (
            <Card key={title} className="p-8 rounded-2xl border-border bg-white" data-testid={`mvv-${title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="w-14 h-14 rounded-2xl bg-primary text-white flex items-center justify-center mb-5">
                <Icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Objectives */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Objectives</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2" style={{ fontFamily: "Outfit" }}>
              What we work towards every day
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Aggregate farmers and create collective bargaining power",
              "Provide quality agricultural inputs at affordable prices",
              "Promote Indapur Agro Feed across the dairy ecosystem",
              "Build digital connectivity and farmer registration network",
              "Improve farmer income through direct market linkages",
              "Conduct training on sustainable and modern farming practices",
            ].map((o, i) => (
              <Card key={o} className="p-6 rounded-2xl border-border hover:shadow-md transition-shadow" data-testid={`objective-${i}`}>
                <div className="text-3xl font-bold text-primary/20 mb-2" style={{ fontFamily: "Outfit" }}>
                  0{i + 1}
                </div>
                <p className="text-sm text-foreground leading-relaxed">{o}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
